import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { color, compose, layout, position, space } from 'styled-system';

import Text from '../Text';

const baseStyles = ({ theme }) => css`
  align-items: center;
  display: inline-flex;
  text-decoration: none;
  width: fit-content;

  &:active,
  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: 1px solid ${theme.colors.link.focus.border};
  }
`;

const typeStyles = ({ theme, type }) => {
  const linkTypesStylesMap = {
    primary: css`
      color: ${theme.colors.link.primary.foreground};

      .shopper-icon {
        fill: ${theme.colors.link.primary.foreground};
      }

      &:active {
        color: ${theme.colors.link.primary.foregroundActive};
      }

      &:hover {
        color: ${theme.colors.link.primary.foregroundHover};

        .shopper-icon {
          fill: ${theme.colors.link.primary.foregroundHover};
        }
      }
    `,
    secondary: css`
      color: ${theme.colors.link.secondary.foreground};

      .shopper-icon {
        fill: ${theme.colors.link.secondary.foreground};
      }

      &:active {
        color: ${theme.colors.link.secondary.foregroundActive};
      }

      &:hover {
        color: ${theme.colors.link.secondary.foregroundHover};

        .shopper-icon {
          fill: ${theme.colors.link.secondary.foregroundHover};
        }
      }
    `,
  };

  return type && linkTypesStylesMap[type];
};

const StyledLink = styled(Text)`
  ${baseStyles}
  ${typeStyles}
  ${compose(color, space, layout, position)}
`;

const Link = forwardRef(({ children, href, size, onClick, ...rest }, ref) => {
  // https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/master/docs/rules/anchor-is-valid.md
  const htmlMarkup = !href ? 'button' : 'a';

  return (
    <StyledLink
      as={htmlMarkup}
      href={href}
      size={size}
      ref={ref}
      onClick={onClick}
      {...rest}
    >
      {children}
    </StyledLink>
  );
});

Link.displayName = 'Link';
Link.SIZES = ['size1', 'size2', 'size3', 'size4'];
Link.TYPES = ['primary', 'secondary'];

Link.defaultProps = {
  href: null,
  size: 'size1',
  target: null,
  type: 'primary',
  onClick: () => {},
};

Link.propTypes = {
  /**
   * String or component to render
   */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /**
   * A valid URL that the anchor points to
   */
  href: PropTypes.string,
  /**
   * Renders Link in different sizes
   */
  size: PropTypes.oneOf(['size1', 'size2', 'size3', 'size4']),
  /**
   * Renders `target` HTML attribute
   */
  target: PropTypes.string,
  /**
   * Whether link has primary or secondary variant
   */
  type: PropTypes.oneOf(['primary', 'secondary']),
  /**
   * Callback to be executed on click
   */
  onClick: PropTypes.func,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
  /**
   * Implements `styled-system` [position](https://styled-system.com/api/#position) utility
   */
  ...propTypes.position,
};

export default Link;
